/**
 * The status label shown on a CHARGE in billing history, its pop-up and Future
 * Invoices: Draft / Due / Paid / Partially paid / Canceled / Payment plan.
 *
 * Billing history showed no status at all (2026-10-10): drafts, paid,
 * outstanding and canceled charges looked the same, and a draft counts toward
 * the balance once it is due. This reads the STORED values only
 * (invoices.status: paid, outstanding, partially_paid, pending, cancelled,
 * waived, payment_plan, refunded; post_status: draft). "unpaid" and
 * "overdue" are never stored, so they are not read here.
 *
 * Payment lines (credits) have no charge status: returns null for them.
 *
 * A club that keeps an account balance records payments onto the balance, so
 * a charge its money paid keeps the stored status "outstanding" (PM 3ebf2943:
 * a family that had paid everything but a $1 water saw September dues,
 * October dues and a uniform as "Due"). For those clubs the backend sends
 * `payment_state` ("paid" / "partially_paid" / "unpaid"), worked out by the
 * one allocation (apps/backend services/accountAllocation: money that names
 * its charge pays it, then the oldest charges first), and `amount_remaining`.
 * That is shown instead of the stored outstanding / pending / partially paid /
 * paid status. Drafts, canceled, waived, refunded and payment-plan charges
 * keep their own label.
 */
export type ChargeStatusKey =
  | "draft"
  | "due"
  | "paid"
  | "partially_paid"
  | "canceled"
  | "payment_plan"
  | "waived"
  | "refunded";

export interface ChargeStatus {
  key: ChargeStatusKey;
  label: string;
  /** Tailwind classes for the theme asked for. Semantic status colors, not brand. */
  className: string;
  /** A hover line, e.g. "$85.00 still owed" on a partly paid balance-club charge. */
  title?: string;
}

/** Stored statuses the allocated payment state stands in for. */
const ALLOCATED_OVER = new Set(["outstanding", "pending", "partially_paid", "paid"]);
const KEY_OF_PAYMENT_STATE: Record<string, ChargeStatusKey> = {
  paid: "paid",
  partially_paid: "partially_paid",
  unpaid: "due",
};

const STYLES: Record<ChargeStatusKey, [string, string]> = {
  // [light, dark]
  draft: ["bg-gray-200 text-gray-800", "bg-gray-700 text-gray-200"],
  due: ["bg-amber-100 text-amber-800", "bg-amber-900 text-amber-200"],
  paid: ["bg-green-100 text-green-800", "bg-green-900 text-green-200"],
  partially_paid: ["bg-yellow-100 text-yellow-800", "bg-yellow-900 text-yellow-200"],
  canceled: ["bg-gray-100 text-gray-600 line-through", "bg-gray-800 text-gray-400 line-through"],
  payment_plan: ["bg-indigo-100 text-indigo-800", "bg-indigo-900 text-indigo-200"],
  waived: ["bg-gray-100 text-gray-700", "bg-gray-800 text-gray-300"],
  refunded: ["bg-purple-100 text-purple-800", "bg-purple-900 text-purple-200"],
};

const LABELS: Record<ChargeStatusKey, string> = {
  draft: "Draft",
  due: "Due",
  paid: "Paid",
  partially_paid: "Partially paid",
  canceled: "Canceled",
  payment_plan: "Payment plan",
  waived: "Waived",
  refunded: "Refunded",
};

export function isCreditLine(invoice: any): boolean {
  return invoice?.type === "credit" || invoice?.metadata?.type === "credit";
}

export function chargeStatus(invoice: any, isDarkMode = false): ChargeStatus | null {
  if (!invoice || isCreditLine(invoice)) return null;
  const status = String(invoice.status || "");
  let key: ChargeStatusKey;
  if (status === "cancelled") key = "canceled";
  else if (invoice.post_status === "draft") key = "draft";
  else if (ALLOCATED_OVER.has(status) && KEY_OF_PAYMENT_STATE[invoice.payment_state]) {
    key = KEY_OF_PAYMENT_STATE[invoice.payment_state];
  } else if (status === "paid") key = "paid";
  else if (status === "partially_paid") key = "partially_paid";
  else if (status === "payment_plan") key = "payment_plan";
  else if (status === "waived") key = "waived";
  else if (status === "refunded") key = "refunded";
  else key = "due"; // outstanding, pending, or anything not yet settled
  const out: ChargeStatus = { key, label: LABELS[key], className: STYLES[key][isDarkMode ? 1 : 0] };
  const remaining = Number(invoice.amount_remaining);
  if (key === "partially_paid" && invoice.payment_state && Number.isFinite(remaining) && remaining > 0) {
    out.title = `$${remaining.toFixed(2)} still owed`;
  }
  return out;
}
